// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0

import { VerificationFlow, UpdateVerificationFlowBody } from '@ory/client'
import { UserAuthCard } from '@ory/elements'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sdk, useSdkError } from '../services/sdk'
import AuthLayout from '../components/layout/AuthLayout'
import { SpinnerIcon } from '@sistemiv/s-components'

export const Verification = (): JSX.Element => {
  const [flow, setFlow] = useState<VerificationFlow | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const returnTo = searchParams.get('return_to')
  const flowId = searchParams.get('flow')

  const navigate = useNavigate()

  // Get the flow based on the flowId in the URL (.e.g redirect to this page after flow initialized)
  const getFlow = useCallback(
    (flowId: string) =>
      sdk
        // the flow data contains the form fields, error messages and csrf token
        .getVerificationFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    // eslint-disable-next-line
    [],
  )

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/verification', true)

  // create a new verification flow
  const createFlow = React.useCallback(() => {
    sdk
      .createBrowserVerificationFlow({
        ...(returnTo && { returnTo: returnTo }),
      })
      // flow contains the form fields, error messages and csrf token
      .then(({ data: flow }) => {
        // Update URI query params to include flow id
        setSearchParams({ ['flow']: flow.id })
        // Set the flow data
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }, [sdkErrorHandler, setSearchParams, setFlow, returnTo])

  // submit the verification form data to Ory
  const submitFlow = (body: UpdateVerificationFlowBody) => {
    // something unexpected went wrong and the flow was not set
    if (!flow) return navigate('/verification', { replace: true })

    sdk
      .updateVerificationFlow({
        flow: flow.id,
        updateVerificationFlowBody: body,
      })
      .then(({ data: flow }) => {
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }

  useEffect(() => {
    if (!flow) {
      // it could happen that we are redirected here with an existing flow
      if (flowId) {
        // if the flow failed to get since it could be expired or invalid, we create a new one
        getFlow(flowId).catch(createFlow)
        return
      }
      createFlow()
    }
  }, [createFlow, getFlow, flowId, flow])

  // if the flow is not set, we show a loading indicator
  return (
    <AuthLayout>
      {flow ? (
        <UserAuthCard
          flowType={'verification'}
          flow={flow}
          className='custom-auth-card !min-w-[calc(100vw-4rem)] sm:!min-w-[400px] p-4 sm:p-6 rounded-md sm:rounded-lg'
          additionalProps={{
            signupURL: {
              handler: () => {
                navigate({ pathname: '/registration' }, { replace: true })
              },
            },
          }}
          // submit the verification form data to Ory
          onSubmit={({ body }) => submitFlow(body as UpdateVerificationFlowBody)}
        />
      ) : (
        <SpinnerIcon className='w-6 h-6 text-sky-500 animate-spin' />
      )}
    </AuthLayout>
  )
}
